/*
 * Copyright (c) 2022, WSO2 LLC. (http://www.wso2.com). All Rights Reserved.
 *
 * This software is the property of WSO2 Inc. and its suppliers, if any.
 * Dissemination of any information or reproduction of any material contained
 * herein is strictly forbidden, unless permitted by WSO2 in accordance with
 * the WSO2 Commercial License available at http://wso2.com/licenses.
 * For specific language governing the permissions and limitations under
 * this license, please see the license as well as any agreement you’ve
 * entered into with WSO2 governing the purchase of this software and any
 * associated services.
 */

import logger from 'utils/logger';

/**
 * You can find your cookie category IDs under Categorizations > Categories.
 * https://app.cookiepro.com/cookies/categorizations?tab=Categories
 * @export
 * @enum {number}
 */
export enum CookieProCategory {
  /**
   * Strictly Necessary Cookies:
   * These cookies are necessary for the website to function and cannot be switched off in our systems.
   * They are usually only set in response to actions made by you which amount to a request for services,
   * such as setting your privacy preferences, logging in or filling in forms. You can set your browser to block
   * or alert you about these cookies, but some parts of the site will not then work. These cookies do not
   * store any personally identifiable information.
   */
  C0001 = 'Strictly Necessary Cookies',

  /**
   * Performance Cookies:
   * These cookies allow us to count visits and traffic sources so we can measure and improve the
   * performance of our site. They help us to know which pages are the most and least popular and see
   * how visitors move around the site. All information these cookies collect is aggregated and therefore anonymous.
   * If you do not allow these cookies we will not know when you have visited our site,
   * and will not be able to monitor its performance.
   */
  C0002 = 'Performance Cookies',

  /**
   * Functional Cookies:
   * These cookies enable the website to provide enhanced functionality and personalization.
   * They may be set by us or by third party providers whose services we have added to our pages.
   * If you do not allow these cookies then some or all of these services may not function properly.
   */
  C0003 = 'Functional Cookies',

  /**
   * Targeting Cookies:
   * These cookies may be set through our site by our advertising partners.
   * They may be used by those companies to build a profile of your interests and show you relevant adverts
   * on other sites. They do not store directly personal information, but are based on uniquely identifying
   * your browser and internet device. If you do not allow these cookies,
   * you will experience less targeted advertising.
   */
  C0004 = 'Targeting Cookies',

  /**
   * Social Media Cookies:
   * These cookies are set by a range of social media services that we have added to the site to enable you to share
   * our content with your friends and networks. They are capable of tracking your browser across other sites and
   * building up a profile of your interests. This may impact the content and messages you see on other websites
   * you visit.If you do not allow these cookies you may not be able to use or see these sharing tools.
   */
  C0005 = 'Social Media Cookies',
}

/**
 *
 * Return the user consented cookie categories for current browser session.
 * We can read the Allowed (consented) cookie categories from the `OnetrustActiveGroups` global variable.
 * OneTrust Cookie Consent populates a data layer object OnetrustActiveGroups,
 * with the cookie categories that the user has consented to.
 * For more information about this global variable please refer OneTrust CookiePro docs
 * https://community.cookiepro.com/s/article/UUID-66bcaaf1-c7ca-5f32-6760-c75a1337c226?language=en_US
 * @export
 */

export function getUserAllowedCategories() {
  if (typeof OnetrustActiveGroups === 'undefined') {
    // TODO tmkasun: Monitor sentry logs for frequency of this error.
    // If the frequency is high we might need to asynchronous wait for CookiePro widget to be initialized
    logger.error('Cookie pro widget is not initialized');
    return null;
  }
  const activeGroups = OnetrustActiveGroups.split(',').filter((group) => group);
  return activeGroups.map((group) => {
    const category: CookieProCategory =
      CookieProCategory[
        group.toLocaleUpperCase() as keyof typeof CookieProCategory
      ];
    return category;
  });
}

export function isStrictlyNecessaryCookiesAllowed() {
  return getUserAllowedCategories()?.includes(CookieProCategory.C0001);
}

export function isPerformanceCookiesAllowed() {
  return getUserAllowedCategories()?.includes(CookieProCategory.C0002);
}

export function isFunctionalCookiesAllowed() {
  return getUserAllowedCategories()?.includes(CookieProCategory.C0003);
}

export function isTargetingCookiesAllowed() {
  return getUserAllowedCategories()?.includes(CookieProCategory.C0004);
}

export function isSocialMediaCookiesAllowed() {
  return getUserAllowedCategories()?.includes(CookieProCategory.C0005);
}
